<template>
  <!-- The live region stays in the page so a screen reader announces the text when it appears. -->
  <div role="status" aria-live="polite" data-testid="new-version-status">
    <q-banner
      v-if="visible"
      dense
      class="bg-amber-8 text-black"
      data-testid="new-version-banner"
    >
      A new version is available.
      <template #action>
        <q-btn
          flat
          dense
          no-caps
          label="Refresh"
          data-testid="new-version-refresh"
          @click="refresh"
        />
        <q-btn
          flat
          dense
          no-caps
          label="Dismiss"
          data-testid="new-version-dismiss"
          @click="dismiss"
        />
      </template>
    </q-banner>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw } from 'vue';
import {
  NewVersionNotice,
  VersionWatcher,
  runningBuildId,
} from '@/utils/VersionCheck';

/**
 * EN-1862: tells the user the app is not the latest deployed build and offers
 * a Refresh. It sits at the bottom of the main layout's header, so it takes its
 * own row instead of covering the header buttons, and it is only mounted after
 * login, so mounting is the "right after login" check. It never reloads by
 * itself.
 */
export default defineComponent({
  name: 'NewVersionBanner',
  data() {
    return {
      notice: new NewVersionNotice(),
      // Not reactive: the watcher holds timers and listeners, not view state.
      watcher: null as VersionWatcher | null,
    };
  },
  computed: {
    visible(): boolean {
      return this.notice.visible;
    },
  },
  mounted() {
    this.watcher = markRaw(
      new VersionWatcher({
        runningBuildId: runningBuildId(),
        fetchImpl: (input, init) => fetch(input, init),
        onCheck: (available) => this.notice.onCheck(available),
      })
    );
    this.watcher.start();
  },
  beforeUnmount() {
    this.watcher?.stop();
    this.watcher = null;
  },
  methods: {
    refresh() {
      this.notice.refresh(() => window.location.reload());
    },
    dismiss() {
      this.notice.dismiss();
    },
  },
});
</script>
